import {useState, useEffect, useReducer, useContext} from 'react'
import {MilestoneContext} from '../components/Milestone/Milestone'
import {MilestoneSectionContext} from '../components/Milestone/Milestone'
import Moment from 'moment'
import get from 'get-value'
import set from 'set-value'

export const useMilestoneSectionContext = tabName => {
    const {
        sectionFormState,
        setMilestoneFormDataChanged,
        removeMilestoneFormDataChanged,
    } = useContext(MilestoneSectionContext)
    const [msValid, setMsValid] = useState(false)

    useEffect(() => {
        if (msValid) setMilestoneFormDataChanged(tabName)
        else if (sectionFormState.length > 0) {
            removeMilestoneFormDataChanged(tabName)
        }
    }, [msValid])

    return setMsValid
}

export const useMilestoneValidation = tabName => {
    const {updateValidation, updateValidationMsg} = useContext(MilestoneContext)
    const [msValid, setMsValid] = useState(null)
    const [msValidMsg, setMsValidMsg] = useState('')

    useEffect(() => {
        updateValidation(tabName, msValid)
    }, [msValid])

    useEffect(() => {
        updateValidationMsg(tabName, msValidMsg)
    }, [msValidMsg])

    return {setMsValid, setMsValidMsg}
}

export const useFormReducerHook = () => {
    const initialState = {
        formData: {},
        errorsData: {},
        validationData: {},
        toHide: {},
    }
    const reducer = (state, action) => {
        switch (action.type) {
            case 'setEntireFormData':
                return {
                    ...state,
                    formData: action.value,
                }
            case 'setFormValue':
                const formData = {...state.formData}
                set(formData, action.key, action.value)
                return {
                    ...state,
                    formData: formData,
                    errorsData: {...state.errorsData, [action.key]: ''},
                }
            case 'setValidationValue':
                return {
                    ...state,
                    validationData: {...state.validationData, [action.key]: action.value},
                }
            case 'setErrorValue':
                return {
                    ...state,
                    errorsData: {...state.errorsData, [action.key]: action.value},
                }
            case 'validate':
                return state
            case 'setBulkFormValue':
                return {
                    ...state,
                    formData: {...state.formData, ...action.keyValueList},
                }
            case 'resetErrorValue':
                return {
                    ...state,
                    errorsData: {},
                }
            case 'setToHide':
                return {...state, toHide: action.value}
            default:
                throw new Error('Unexpected action')
        }
    }
    const [state, dispatch] = useReducer(reducer, initialState)
    useEffect(() => {}, [state.formData])

    const updateToHide = value => {
        dispatch({
            type: 'setToHide',
            value,
        })
    }

    const updateEntireFormData = value => {
        dispatch({
            type: 'setEntireFormData',
            value,
        })
    }

    const updateFormData = (key, value) => {
        dispatch({
            type: 'setFormValue',
            key,
            value,
        })
    }

    const updateValidationData = (key, value) => {
        dispatch({
            type: 'setValidationValue',
            key,
            value,
        })
    }

    const updateErrorData = (key, value) => {
        dispatch({
            type: 'setErrorValue',
            key,
            value,
        })
    }

    const resetErrorData = () => {
        dispatch({
            type: 'resetErrorValue',
        })
    }

    const updateBulkFormData = keyValueList => {
        dispatch({
            type: 'setBulkFormValue',
            keyValueList,
        })
    }

    const validateReqFormFieldStatus = () => {
        const {formData, validationData, toHide} = state
        let isValid = true
        // const keys = Object.keys(toHide)
        // for (const key of keys) {
        //     if (toHide[key]) {
        //         updateFormData(key, null)
        //     }
        // }
        for (let key in validationData) {
            if (validationData[key] && validationData[key].length) {
                if (toHide[key]) {
                    continue
                }
                for (let i = 0; i < validationData[key].length; i++) {
                    const validationObj = validationData[key][i]
                    const error = checkValidation(
                        get(formData, key),
                        validationObj,
                        validationObj.type === 'compareTo'
                            ? get(formData, validationObj.value)
                            : null
                    )
                    if (error) {
                        //updateErrorData(key, error)
                        isValid = false
                        break
                    }
                }
            }
        }
        return isValid
    }

    const validate = () => {
        const {formData, validationData, toHide} = state
        let isValid = true
        resetErrorData()
        const keys = Object.keys(toHide)
        for (const key of keys) {
            if (toHide[key]) {
                updateFormData(key, null)
            }
        }
        for (let key in validationData) {
            if (validationData[key] && validationData[key].length) {
                if (toHide[key]) {
                    continue
                }
                for (let i = 0; i < validationData[key].length; i++) {
                    const validationObj = validationData[key][i]
                    const error = checkValidation(
                        get(formData, key),
                        validationObj,
                        validationObj.type === 'compareTo' ||
                            validationObj.type === 'compareDate'
                            ? get(formData, validationObj.value)
                            : null
                    )
                    if (error) {
                        updateErrorData(key, error)
                        isValid = false
                        break
                    }
                }
            }
        }

        return isValid
    }

    const validateForKey = key => {
        const {formData, validationData} = state
        if (validationData[key] && validationData[key].length) {
            for (let i = 0; i < validationData[key].length; i++) {
                const validationObj = validationData[key][i]
                const error = checkValidation(
                    get(formData, key),
                    validationObj,
                    validationObj.type === 'compareTo' ||
                        validationObj.type === 'compareDate'
                        ? get(formData, validationObj.value)
                        : null
                )
                if (error) {
                    updateErrorData(key, error)
                    break
                } else if (error === '' && i === validationData[key].length - 1) {
                    updateErrorData(key, '')
                    break
                }
            }
        }
    }

    const internalCheckToShow = props => {
        const {toHide, formData} = state
        if (!checkToShow(props, formData)) {
            props.columnName && (toHide[props.columnName] = true)
            if (props.skipValidateOnHide) {
                props.skipValidateOnHide.forEach(c => {
                    toHide[c] = true
                })
            }
            updateToHide(toHide)
        } else {
            props.columnName && (toHide[props.columnName] = false)
            if (props.skipValidateOnHide) {
                props.skipValidateOnHide.forEach(c => {
                    toHide[c] = false
                })
            }
            updateToHide(toHide)
        }
    }

    return {
        formData: state.formData,
        updateFormData,
        errorsData: state.errorsData,
        validate,
        validationData: state.validationData,
        updateValidationData,
        updateBulkFormData,
        updateEntireFormData,
        internalCheckToShow,
        toHide: state.toHide,
        validateForKey,
        updateErrorData,
        validateReqFormFieldStatus,
    }
}

export const parseIntegerOnly = value => {
    return value.replace(/[^A-Z0-9]/gi, '')
}

const checkValidation = (value, validationObject, compareToValue) => {
    switch (validationObject.type) {
        case 'checkEmail':
           console.log("checkingemail:"+value);
           const emailRegex = /^([a-zA-Z0-9_\-\.]+)*@[a-zA-Z0-9]+([\-\.][a-zA-Z0-9]+)*(\.[a-zA-Z]{2,15})$/
         //const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,10})+$/

            if (value) {
                if (!emailRegex.test(value))
                    return (
                        validationObject.errorText ||
                        `Regular expression validation failed for this field`
                    )
            }
            return ''
        case 'checkAge':
            var calculateAge = Moment().subtract('years', 18)
            var selectDate = Moment(value)

            if (!selectDate.isValid()) {
                // INVALID DATE
            } else if (calculateAge.isAfter(selectDate)) {
                // 18+
                return ''
            } else {
                // < 18
                return validationObject.errorText || `Your under age`
            }
            return ''
        case 'checkMinDate':
            const minDate = new Date()
            const inputDate = value === null ? null : new Date(value.replace(/-/g, '/'))
            const currentDate = new Date(
                minDate.getFullYear(),
                minDate.getMonth(),
                minDate.getDate()
            )
            return value === null
                ? ''
                : inputDate.getTime() < currentDate.getTime()
                ? validationObject.errorText || `Date should not be in the past`
                : ''
        case 'checkMaxDate':
            const maxDate = new Date()
            const selectedDate =
                value === null ? null : new Date(value.replace(/-/g, '/'))
            const currentMDate = new Date(
                maxDate.getFullYear(),
                maxDate.getMonth(),
                maxDate.getDate()
            )
            return value === null
                ? ''
                : selectedDate.getTime() > currentMDate.getTime()
                ? validationObject.errorText || `Date should not be in the future`
                : ''
        case 'checkRegex':
            const customRegx = new RegExp(validationObject.value)
            const combination1 = validationObject.isOnlyNumberAllow
                ? /^[ '"/,!`~@#$%^&*()_+={}[\]|\\:;<>?&.-]+$/
                : /^[ 0-9'"/,!`~@#$%^&*()_+={}[\]|\\:;<>?&.-]+$/
            if (value) {
                if (!customRegx.test(value))
                    return (
                        validationObject.errorText ||
                        `Regular expression validation failed for this field`
                    )
                else if (value.match(combination1))
                    return `Are you sure you entered your details correctly?`
            }
            return ''
        case 'compareDate':
            if (value && compareToValue) {
                const date1 = new Date(value.replace(/-/g, '/'))
                const date2 = new Date(compareToValue.replace(/-/g, '/'))
                const date3 = new Date()
                const date4 = new Date(date3.getFullYear(), 0, 1)
                if (validationObject.comparisonOperator === '>') {
                    if (date2.getTime() > date1.getTime())
                        return validationObject.errorText
                    if (date1.getTime() === date2.getTime())
                        return validationObject.errorText
                    if (validationObject.value === 'validfromdate') {
                        if (date4.getTime() > date1.getTime())
                            return (
                                'Valid To date should be greater than or equal to ' +
                                date4
                                    .toDateString()
                                    .split(' ')
                                    .slice(1)
                                    .join(' ')
                            )
                    }
                } else {
                    if (date1.getTime() > date2.getTime())
                        return validationObject.errorText
                    if (date1.getTime() === date2.getTime())
                        return validationObject.errorText
                }
            }
            return ''
        case 'integerOnly':
            const customIntRegx = /^[0-9\b]+$/
            if (!customIntRegx.test(value))
                return (
                    validationObject.errorText ||
                    `Regular expression validation failed for this field`
                )

            return ''
        case 'stringOnly':
            const customStrRegx = /^[A-Za-z'.-\s]+$/
            const combination = /^[ .'-]+$/
            if (!customStrRegx.test(value))
                return (
                    validationObject.errorText ||
                    `Regular expression validation failed for this field`
                )
            else if (value.match(combination))
                return (
                    validationObject.errorText ||
                    `Regular expression validation failed for this field`
                )

            return ''
        case 'alphaNumeric':
            const customalphaNumRegx = /^[A-Za-z\d\s]*$/
            if (!customalphaNumRegx.test(value))
                return (
                    validationObject.errorText ||
                    `Regular expression validation failed for this field`
                )

            return ''
        case 'alphaSpecial':
            const customalphaSpRegx = /^[A-Za-z\d\s]*$/
            if (!customalphaSpRegx.test(value))
                return (
                    validationObject.errorText ||
                    `Regular expression validation failed for this field`
                )

            return ''
        case 'date':
            if (new Date(value).toDateString() === 'Invalid Date') return true
            return false
        case 'phoneMinLength':
            if (value && value.trim() !== '+1') {
                const componentValue = String(value).split('x')
                const [val1, val2] = componentValue
                const checkForExtn = val1.startsWith('+') ? val1.substring(2) : val1
                const phone = parseIntegerOnly(checkForExtn)
                if (phone) {
                    if (phone.length < validationObject.value)
                        return (
                            validationObject.errorText ||
                            `Number must be at least ${validationObject.value} digits`
                        )
                }
            }
            return ''
        case 'minLength':
            if (value === null || value === undefined) return ''

            if (
                String(value).length > 0 &&
                String(value).length < validationObject.value
            ) {
                return (
                    validationObject.errorText ||
                    `This field must be atleast ${validationObject.value} characters`
                )
            }
            return ''
        case 'nameString':
            const combination2 = /^[ 0-9'"/,!`~@#$%^&*()_+={}[\]|\\:;<>?&.-]+$/
            if (value) {
                const customRegx = new RegExp("^[ A-Za-z'.-]+$")
                if (!customRegx.test(value))
                    return (
                        validationObject.errorText ||
                        "Only (' . -) characters and alphabets are allowed"
                    )
                else if (value.match(combination2))
                    return `Are you sure you entered your details correctly?`
            }
            return ''
        case 'providerName':
            const restrictRegex = /([\d<>])/gm
            if (value && restrictRegex.test(value))
                return validationObject.errorText || 'This field is invalid'
            return ''
        case 'webURL':
            const websiteReGX = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/gi
            if (value) {
                if (!websiteReGX.test(value))
                    return validationObject.errorText || 'Invalid URL'
            }
            return ''
        case 'isRequired':
            if (typeof value === 'boolean') return ''
            else if (!value) return validationObject.errorText || 'This field is required'

            if (typeof value === 'object') {
                if (
                    (Object.entries(value).length === 0 &&
                        (value.constructor === Object || value.constructor === Array)) ||
                    Object.values(value).every(o => o === null)
                )
                    return validationObject.errorText || 'This field is required'
            }
            return ''
        case 'maxLength':
            if (value === null || value === undefined) return ''

            if (value.length > validationObject.value)
                return (
                    validationObject.errorText ||
                    `This field must be less than ${validationObject.value} characters`
                )

            return ''
        case 'regex':
            if (value) {
                if (!regexValidation(value, validationObject.value))
                    return validationObject.errorText || 'Enter correct value'
            }
            return ''
        case 'regexwithlower':
            value = String(value).toLowerCase()
            if (!regexValidation(value, validationObject.value))
                return validationObject.errorText || 'Enter correct value'

            return ''
        case 'compareTo':
            if (
                validationObject.compareType &&
                validationObject.compareType === 'email'
            ) {
                if (value.toLowerCase() !== compareToValue.toLowerCase())
                    return validationObject.errorText || 'Enter same value'
            } else if (value !== compareToValue)
                return validationObject.errorText || 'Enter same value'

            return ''
        case 'validateOnBlur':
            return ''

        case 'filenameextesnionregex':
            if (typeof value === 'object') {
                if (
                    value &&
                    value.length > 0 &&
                    value[0] &&
                    value[0].name &&
                    value[0].name
                ) {
                    value = value[0].name.toLowerCase()
                    if (!new RegExp(validationObject.value).test(String(value)))
                        return validationObject.errorText || 'Upload valid file'
                }
            }
            return ''
        case 'dotallowedinfilenameregex':
            if (typeof value === 'object') {
                if (
                    value &&
                    value.length > 0 &&
                    value[0] &&
                    value[0].name &&
                    value[0].name
                ) {
                    value = value[0].name.toLowerCase()
                    if (new RegExp(validationObject.value).test(String(value)))
                        return validationObject.errorText || 'Upload valid file'
                }
            }
            return ''

        default:
            // console.log('NEWLY ADDED VALIDATION TYPE:', validationObject.type)
            throw new Error('Invlid validation type encountered')
    }
}

// useRequiredHook is for check required field
export const useRequiredHook = validationProps => {
    const [required, setRequired] = useState(false)

    useEffect(() => {
        if (validationProps && validationProps.length) {
            validationProps.map(item => {
                if (item.type === 'isRequired') {
                    setRequired(true)
                }
            })
        }
    }, [validationProps])

    return {required}
}

export const checkToShow = (props, formData) => {
    if (props.showOnKey) {
        let showOnKeyValue = get(formData, props.showOnKey)
        if (props.showForCondition) {
            switch (props.showForCondition) {
                case 'not-empty':
                    if (showOnKeyValue) return true
                    else return false
                case 'not-equal':
                    if (String(showOnKeyValue) !== String(props.showForValue)) return true
                    else return false
                default:
                    return true
            }
        } else if (props.showForValue) {
            if (String(showOnKeyValue) === String(props.showForValue)) return true
            else return false
        } else {
            return false
        }
    } else return true
}

export const checkToDisable = (props, formData) => {
    if (props.disableOnKey) {
        let disableOnKeyValue = get(formData, props.disableOnKey)
        if (props.disableForCondition) {
            switch (props.disableForCondition) {
                case 'is-empty':
                    if (!disableOnKeyValue) return true
                    else return false
                case 'not-empty':
                    if (disableOnKeyValue) return true
                    else return false
                case 'not-equal':
                    // if (!props.disableForConditionValue || !disableOnKeyValue)
                    //     return false

                    if (
                        String(disableOnKeyValue) !==
                        String(props.disableForConditionValue)
                    )
                        return true
                    else return false

                default:
                    return true
            }
        } else if (props.disableForValue) {
            if (
                String(disableOnKeyValue) === String(props.disableForValue) ||
                String(disableOnKeyValue) === 'null'
            )
                return true
            else return false
        } else {
            return false
        }
    } else return true
}

// export const checkRegexValidation = (email, regexType) => {
//     switch (regexType.toLowerCase()) {
//         case 'email':
//             var emailRegex = /^[a-zA-Z0-9]+@[a-zA-Z0-9]+\.[A-Za-z]+$/
//             const checkValid = emailRegex.test(String(email).toLowerCase())
//             return checkValid

//         default:
//             break
//     }
// }

export const regexValidation = (value, regex) => new RegExp(regex).test(String(value))

const tableSort = (array, cmp) => {
    const stabilizedThis = array.map((el, index) => [el, index])
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0])
        if (order !== 0) return order
        return a[1] - b[1]
    })
    return stabilizedThis.map(el => el[0])
}

const getSorting = tableProps => {
    return tableProps.order === 'desc'
        ? (a, b) => desc(a, b, tableProps)
        : (a, b) => -desc(a, b, tableProps)
}

const desc = (a, b, tableProps) => {
    let filterKeys = {}
    filterKeys.mapProperty = tableProps.orderBy
    filterKeys.objectKeyToShow = tableProps.objectKeyToShow
    let aOrderBy = getColumsData(a, filterKeys)
    let bOrderBy = getColumsData(b, filterKeys)
    //if (aOrderBy === null || bOrderBy === null) return 1
    if (bOrderBy < aOrderBy) return -1
    if (bOrderBy > aOrderBy) return 1
    return 0
}

export const getFilterSortedData = (data, tableProperties) => {
    try {
        if (data.length > 0) {
            let sorting = getSorting(tableProperties)
            let sData = tableSort(data, sorting).slice(
                tableProperties.activePage * tableProperties.rowsPerPage,
                tableProperties.activePage * tableProperties.rowsPerPage +
                    tableProperties.rowsPerPage
            )

            if (sData.length === 0 && tableProperties.activePage > 0) {
                tableProperties.activePage = tableProperties.activePage - 1
                sData = tableSort(data, sorting).slice(
                    tableProperties.activePage * tableProperties.rowsPerPage,
                    tableProperties.activePage * tableProperties.rowsPerPage +
                        tableProperties.rowsPerPage
                )
                return sData
            } else return sData
        } else return []
    } catch (e) {
        return []
    }
}

export const getFilterSortedAllData = (data, tableProperties) => {
    try {
        if (data.length > 0) {
            return tableSort(data, getSorting(tableProperties))
        } else return []
    } catch (e) {
        return []
    }
}

const getColumsData = (data, filterKeys) => {
    const cellkey = filterKeys.mapProperty.split(',')
    if (cellkey.length > 1) {
        let returnValue = ''
        cellkey.forEach(element => {
            let elementData = get(data, element)
            if (elementData) returnValue += `${elementData} `
        })
        return returnValue === null ? '' : returnValue
    } else {
        let dateColData = null
        if (filterKeys.dateColumn) {
            dateColData =
                Moment(get(data, filterKeys.mapProperty)).format(
                    (filterKeys.dateFormat && filterKeys.dateFormat) || 'MM/DD/YYYY'
                ) === 'Invalid date'
                    ? (filterKeys.textForInvalidDate && filterKeys.textForInvalidDate) ||
                      ''
                    : Moment(get(data, filterKeys.mapProperty)).format(
                          (filterKeys.dateFormat && filterKeys.dateFormat) || 'MM/DD/YYYY'
                      )
        }
        const keyCellValue =
            (dateColData === null && get(data, filterKeys.mapProperty)) || dateColData

        if (Array.isArray(keyCellValue)) {
            return keyCellValue.map(p => p[filterKeys.objectKeyToShow]).join(', ')
        } else return keyCellValue === null ? '' : keyCellValue
    }
}

const nestedFilter = (targetArray, filterKeys, searchData) => {
    let filterData = []
    filterKeys.map(eachKey => {
        let fData = targetArray.filter(eachObj => {
            let getValue = getColumsData(eachObj, eachKey)
            return getValue
                ? String(getValue)
                      .toLowerCase()
                      .indexOf(searchData) !== -1
                : false
        })
        if (fData.length > 0) {
            fData.map(d => {
                filterData.push(d)
            })
            targetArray = targetArray.filter(eachObj => {
                let getValue = getColumsData(eachObj, eachKey)

                return getValue
                    ? String(getValue)
                          .toLowerCase()
                          .indexOf(searchData) === -1
                    : false
            })
        }
    })
    return filterData
}

export const searchOnData = (searchText, columns, data) => {
    if (searchText && searchText.trim() !== '') {
        let colList = columns.filter(e => {
            return e.isFilter
        })
        // let filterKeys = colList.map(e => {
        //     return e.mapProperty
        // })
        return nestedFilter(data, colList, searchText.toLowerCase())
    } else return data
}

export const getConvertToArray = source => {
    let array
    if (!source) array = []
    else if (source === '') array = []
    else {
        if (Array.isArray(source)) {
            array = source
        } else {
            if (
                String(source)
                    .trim()
                    .slice(-1) !== ','
            ) {
                source = source + ','
            }
            array = source.split(',')
        }
    }
    return array
}

export const useStickyState = (defaultValue, key) => {
    const [value, setValue] = useState(() => {
        const stickyValue = window.sessionStorage.getItem(key)

        return stickyValue !== null && stickyValue !== undefined
            ? key === 'unit'
                ? stickyValue
                : JSON.parse(stickyValue)
            : defaultValue
    })
    useEffect(() => {
        if (key === 'unit') window.sessionStorage.setItem(key, value)
        else window.sessionStorage.setItem(key, JSON.stringify(value))
    }, [key, value])
    return [value, setValue]
}
